/**
 * José de Faktelio en la ficha de una oportunidad — `OD-0058-ENMIENDA-01`.
 *
 * - E-03: lee el porcentaje en una frase y dice qué pregunta lo subiría más
 *   (la calcula la API: `lever`).
 * - E-04: pregunta de una en una, como una conversación. Lo respondido queda
 *   arriba y se puede cambiar; lo que se salta queda "para luego". Con las
 *   cinco respondidas, un resumen en una frase.
 * - E-02: debajo de cada respuesta que no es la favorable, un consejo que
 *   rota cada día y con "Otro consejo".
 * - E-05: lo que haría él ahora, que se apunta como tarea de la oportunidad
 *   con un toque, por la ruta normal de tareas (`OD-0050`, `crm.write`).
 * - E-07: "¿Te ha servido?" debajo de cada consejo y del paso, y "Otro
 *   consejo" avisa de cuál se descarta. Son contadores sin ids (`ADR-0024`).
 *
 * Y con la ENMIENDA-02 (2026-10-29):
 *
 * - G-03: con menos de tres respuestas dice que el número es el de la etapa.
 * - G-06: debajo del número, de qué etapa parte y qué lo sube y qué lo baja.
 * - G-07: la pista —lo que más subiría el porcentaje, del X % al Y %— va
 *   arriba, con «Recordármelo», que la apunta como tarea.
 * - G-02: con la cualificación alta en «Identificada», propone «Cualificada».
 * - G-08: ajustado a mano se ve, y se vuelve al cálculo con un toque.
 * - Y al crearla o moverla sin cualificar, invita a responder (`invitacion`).
 *
 * Las reglas están en `ui/jose.ts` y los textos en `t.jose`. Aquí solo se
 * pintan.
 */
import { useMemo, useState } from "react";
import {
  api,
  PREGUNTAS_CUALIFICACION,
  RESPUESTAS_CUALIFICACION,
  type Oportunidad,
  type PreguntaCualificacion,
  type RespuestaCualificacion,
} from "../api/cliente";
import { t } from "../i18n/es";
import foto from "../ui/jose.webp";
import {
  casoDe,
  clavePaso,
  completa,
  diaLocal,
  factores,
  idConsejo,
  indiceConsejo,
  MINIMO_RESPUESTAS,
  pasoDePregunta,
  presupuestoAcabado,
  respondidas,
  resumen,
  siguientePaso,
  siguientePregunta,
  tramoDe,
  type Paso,
} from "../ui/jose";
import { Boton } from "../ui/primitivas";
import { EtiquetaProbabilidad } from "./PantallaOportunidades";

export function FotoJose({ pequena = false }: { pequena?: boolean }) {
  const lado = pequena ? 40 : 56;
  return <img className={`jose-foto${pequena ? " jose-foto-pequena" : ""}`} src={foto} alt={t.jose.foto} width={lado} height={lado} />;
}

/** E-03: lo que dice José del porcentaje. La pista va aparte, en su bloque (G-07). */
function lectura(o: Oportunidad): string {
  if (o.status === "WON") return t.jose.ganada;
  if (o.status === "LOST") return t.jose.perdida;
  if (o.probability === null) return t.jose.sinRespuestas;
  if (o.probabilitySource === "MANUAL") return t.jose.aMano(o.probability, o.calculatedProbability);
  if (o.probabilitySource === "STAGE") {
    const etapa = (t.oportunidades.etapas as Record<string, string>)[o.stage] ?? o.stage;
    return t.jose.provisional(o.probability, etapa, MINIMO_RESPUESTAS - respondidas(o.qualification));
  }
  return t.jose.lectura(t.jose.tramos[tramoDe(o.probability)]);
}

/**
 * G-06: por qué ese número. Solo cuando lo calculan las respuestas: de qué
 * etapa parte, lo que lo sube y lo que lo baja, y el tope si el presupuesto no
 * salió.
 */
function Explicacion({ o }: { o: Oportunidad }) {
  if (o.status !== "OPEN" || o.probabilitySource !== "CALCULATED") return null;
  const f = factores(o.qualification);
  const etapa = (t.oportunidades.etapas as Record<string, string>)[o.stage] ?? o.stage;
  const e = t.jose.explicacion;
  const partes = [
    ...(f.sube.length > 0 ? [e.sube(f.sube.map((p) => e.motivosSube[p]).join(", "))] : []),
    ...(f.baja.length > 0 ? [e.baja(f.baja.map((p) => e.motivosBaja[p]).join(", "))] : []),
  ];
  return (
    <p className="jose-explicacion">
      {o.stageProbability !== null && !presupuestoAcabado(o.quote?.status ?? null) ? <span>{e.base(o.stageProbability, etapa)} </span> : null}
      {partes.length > 0 ? <span>{partes.join(e.separador)}</span> : null}
      {presupuestoAcabado(o.quote?.status ?? null) ? <span> {e.tope}</span> : null}
    </p>
  );
}

/** E-05: el texto de un paso, para decirlo y para apuntarlo. */
export function textoPaso(p: Paso): { readonly frase: string; readonly tarea: string } {
  return p.clave.tipo === "pregunta" ? t.jose.pasos[p.clave.pregunta][p.clave.accion] : t.jose.pasos[p.clave.accion];
}

/**
 * E-07: "¿Te ha servido?". Un toque y queda dicho en esta pantalla; no se pide
 * texto. Con otro `asunto` (otro consejo, otro paso) se vuelve a preguntar: el
 * que lo usa pone `key={asunto}`.
 */
function TeHaServido({ asunto }: { asunto: string }) {
  const [dado, setDado] = useState(false);
  if (dado) return <p className="jose-util ayuda-neutra">{t.jose.util.gracias}</p>;
  const votar = (value: "UP" | "DOWN"): void => {
    setDado(true);
    // Si no llega, no pasa nada: es un contador, y el usuario ya ha dicho lo suyo.
    void api.senalJose({ type: "FEEDBACK", subject: asunto, value });
  };
  return (
    <div className="jose-util" role="group" aria-label={t.jose.util.pregunta}>
      <span className="ayuda-neutra">{t.jose.util.pregunta}</span>
      <button type="button" className="jose-util-boton" aria-label={t.jose.util.si} title={t.jose.util.si} onClick={() => votar("UP")}>
        {t.jose.util.arriba}
      </button>
      <button type="button" className="jose-util-boton" aria-label={t.jose.util.no} title={t.jose.util.no} onClick={() => votar("DOWN")}>
        {t.jose.util.abajo}
      </button>
    </div>
  );
}

function Fichas({
  pregunta,
  marcada,
  deshabilitado,
  onElegir,
  sugerida = null,
}: {
  pregunta: PreguntaCualificacion;
  marcada: RespuestaCualificacion | null;
  deshabilitado: boolean;
  onElegir: (r: RespuestaCualificacion | null) => void;
  /** OD-0048-ENMIENDA-02 F-04: la que José propone. Se resalta; no se marca sola. */
  sugerida?: RespuestaCualificacion | null;
}) {
  return (
    <div className="fichas" role="group" aria-labelledby={`jose-${pregunta}`}>
      {RESPUESTAS_CUALIFICACION.map((r) => (
        <button
          key={r}
          type="button"
          className={r === sugerida && marcada === null ? "ficha ficha-sugerida" : "ficha"}
          aria-pressed={marcada === r}
          disabled={deshabilitado}
          // Tocar la marcada la desmarca: vuelve a "sin responder".
          onClick={() => onElegir(marcada === r ? null : r)}
        >
          {t.oportunidad.cualificacion.respuestas[r]}
        </button>
      ))}
    </div>
  );
}

export function ConversacionJose({
  oportunidad,
  historial = null,
  invitacion = null,
  deshabilitado,
  onResponder,
  onApuntar,
  onVolverAlCalculo,
  onPasarACualificada,
}: {
  oportunidad: Oportunidad;
  /**
   * OD-0048-ENMIENDA-02 F-04: otra oportunidad GANADA del mismo cliente. Con
   * ella, José PROPONE el «Sí» de «¿Repite o viene recomendado?»; la respuesta
   * la sigue dando el usuario (OD-0058 D-02).
   */
  historial?: { readonly ganada: string } | null;
  /** G-08: lo que dice José al crearla o al moverla sin cualificar. */
  invitacion?: string | null;
  deshabilitado: boolean;
  /** Devuelve si se guardó: si no, la pregunta sigue abierta. */
  onResponder(pregunta: PreguntaCualificacion, respuesta: RespuestaCualificacion | null): Promise<boolean>;
  onApuntar(paso: Paso): Promise<boolean>;
  /** G-08: quita el ajuste a mano. */
  onVolverAlCalculo(): void;
  /** G-02: la ruta normal de cambio de etapa. */
  onPasarACualificada(): void;
}) {
  const [saltadas, setSaltadas] = useState<ReadonlySet<PreguntaCualificacion>>(new Set());
  const [editando, setEditando] = useState<PreguntaCualificacion | null>(null);
  const [otros, setOtros] = useState<Readonly<Partial<Record<PreguntaCualificacion, number>>>>({});
  // Lo ya apuntado, por clave de paso: la pista y el paso son botones distintos.
  const [apuntados, setApuntados] = useState<ReadonlySet<string>>(new Set());
  const [apuntando, setApuntando] = useState<string | null>(null);
  const dia = useMemo(() => diaLocal(new Date()), []);

  const q = oportunidad.qualification;
  // Cerrada, José ya no pregunta ni aconseja: se lee lo que se respondió, y para
  // cambiarlo se reabre (lo pidió el propietario tras probarlo en staging).
  const abierta = oportunidad.status === "OPEN";
  const actual = abierta ? siguientePregunta(q, saltadas) : null;
  const pendientes = abierta ? PREGUNTAS_CUALIFICACION.filter((p) => q[p] === null && saltadas.has(p)) : [];
  const paso = siguientePaso(oportunidad);
  // G-07: la pista es la palanca de la API con el paso que la consigue. Si el
  // siguiente paso es ese mismo, solo se enseña la pista.
  const lever = abierta ? oportunidad.lever : null;
  const pista = lever === null || lever.from === null ? null : { ...lever, from: lever.from, paso: pasoDePregunta(lever.question, q[lever.question]) };
  const pasoAparte = paso !== null && (pista === null || clavePaso(paso.clave) !== clavePaso(pista.paso.clave)) ? paso : null;
  const r = resumen(q, oportunidad.probability);
  const sugerencia = (p: PreguntaCualificacion): RespuestaCualificacion | null => (p === "trust" && historial !== null ? "YES" : null);
  const pistaHistorial = (p: PreguntaCualificacion) =>
    sugerencia(p) !== null && historial !== null && q[p] === null ? <p className="jose-sugerencia">{t.jose.repite(historial.ganada)}</p> : null;

  async function responder(p: PreguntaCualificacion, respuesta: RespuestaCualificacion | null): Promise<void> {
    if (await onResponder(p, respuesta)) {
      setEditando(null);
      setApuntados(new Set());
    }
  }

  async function apuntar(p: Paso): Promise<void> {
    const clave = clavePaso(p.clave);
    setApuntando(clave);
    const ok = await onApuntar(p);
    setApuntando(null);
    if (ok) setApuntados((s) => new Set([...s, clave]));
  }

  function botonApuntar(p: Paso, texto: string, apuntadoTexto: string) {
    const clave = clavePaso(p.clave);
    if (apuntados.has(clave)) return <p className="ayuda-neutra">{apuntadoTexto}</p>;
    return (
      <Boton variante="secundario" deshabilitado={deshabilitado || apuntando !== null} onClick={() => void apuntar(p)}>
        {apuntando === clave ? t.jose.apuntando : texto}
      </Boton>
    );
  }

  function consejo(p: PreguntaCualificacion): { readonly id: string; readonly texto: string } | null {
    const caso = casoDe(p, q[p]);
    if (caso === null) return null;
    const lista = t.jose.consejos[p][caso];
    const i = indiceConsejo({ oportunidadId: oportunidad.id, pregunta: p, caso, dia, otro: otros[p] ?? 0, total: lista.length });
    const texto = lista[i];
    return texto === undefined ? null : { id: idConsejo(p, caso, i), texto };
  }

  function otroConsejo(p: PreguntaCualificacion, descartado: string): void {
    void api.senalJose({ type: "TIP_NEXT", subject: descartado });
    setOtros((o) => ({ ...o, [p]: (o[p] ?? 0) + 1 }));
  }

  return (
    <div className="jose" id="cualificacion-jose">
      <div className="jose-cabecera">
        <FotoJose />
        <div className="jose-burbuja jose-burbuja-principal">
          <p className="jose-nombre">{t.jose.nombre}</p>
          {invitacion !== null && abierta ? <p className="jose-invitacion">{invitacion}</p> : null}
          <p>
            {/* A mano, la etiqueta va sin su "· A mano": lo dice la línea de abajo, con la vuelta al cálculo. */}
            <EtiquetaProbabilidad
              valor={oportunidad.probability}
              origen={oportunidad.probabilitySource === "MANUAL" ? null : oportunidad.probabilitySource}
              respondidas={respondidas(q)}
            />{" "}
            {lectura(oportunidad)}
          </p>
          <Explicacion o={oportunidad} />
          {abierta && oportunidad.probabilitySource === "MANUAL" ? (
            <div className="jose-a-mano">
              <span className="insignia crm-a-mano">{t.jose.ajustadoAMano}</span>
              <button type="button" className="jose-cambiar" onClick={onVolverAlCalculo} disabled={deshabilitado}>
                {t.jose.volverAlCalculo(oportunidad.calculatedProbability)}
              </button>
            </div>
          ) : null}
          {abierta && respondidas(q) === 0 && invitacion === null ? <p className="ayuda-neutra">{t.jose.presentacion}</p> : null}
          {abierta && respondidas(q) > 0 ? <p className="ayuda-neutra">{t.jose.guia}</p> : null}
        </div>
      </div>

      {/* G-07: la pista, a la vista: lo que más subiría el porcentaje y cómo conseguirlo. */}
      {pista !== null ? (
        <div className="jose-pista">
          <p className="jose-nombre">{t.jose.pista.titulo}</p>
          <p className="jose-pista-frase">{t.jose.pista[pista.question](pista.from, pista.probability)}</p>
          <p>{textoPaso(pista.paso).frase}</p>
          <div className="jose-pista-pie">
            {botonApuntar(pista.paso, t.jose.pista.recordarmelo, t.jose.pista.recordado)}
            <TeHaServido key={clavePaso(pista.paso.clave)} asunto={clavePaso(pista.paso.clave)} />
          </div>
        </div>
      ) : null}

      {/* G-02: cualificación alta y todavía en «Identificada». */}
      {abierta && oportunidad.suggestedStage === "QUALIFIED" && oportunidad.qualificationLevel !== null ? (
        <div className="jose-propuesta">
          <p>{t.jose.proponerCualificada(oportunidad.qualificationLevel)}</p>
          <Boton variante="secundario" onClick={onPasarACualificada} deshabilitado={deshabilitado}>
            {t.oportunidad.cualificacion.pasarACualificada}
          </Boton>
        </div>
      ) : null}

      <ol className="jose-conversacion">
        {PREGUNTAS_CUALIFICACION.filter((p) => q[p] !== null).map((p) => {
          const respuesta = q[p];
          const c = abierta ? consejo(p) : null;
          return (
            <li key={p} className="jose-turno">
              <div className="jose-fila">
                <p className="jose-pregunta" id={`jose-${p}`}>
                  {t.oportunidad.cualificacion.preguntas[p]}
                </p>
                {editando === p && abierta ? null : (
                  <p className="jose-respuesta">
                    <span className="jose-tu">
                      {t.jose.tu}: <strong>{respuesta === null ? "" : t.oportunidad.cualificacion.respuestas[respuesta]}</strong>
                    </span>
                    {abierta ? (
                      <button type="button" className="jose-cambiar" onClick={() => setEditando(p)} disabled={deshabilitado}>
                        {t.jose.cambiar}
                      </button>
                    ) : null}
                  </p>
                )}
              </div>
              {editando === p && abierta ? (
                <Fichas pregunta={p} marcada={respuesta} deshabilitado={deshabilitado} onElegir={(x) => void responder(p, x)} sugerida={sugerencia(p)} />
              ) : null}
              {c !== null ? (
                <div className="jose-consejo">
                  <p>
                    <strong>{t.jose.consejo}:</strong> {c.texto}
                  </p>
                  <div className="jose-consejo-pie">
                    <TeHaServido key={c.id} asunto={c.id} />
                    <button type="button" className="jose-cambiar" onClick={() => otroConsejo(p, c.id)}>
                      {t.jose.otroConsejo}
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}

        {actual !== null ? (
          <li className="jose-turno jose-turno-actual">
            <div className="jose-cabecera">
              <FotoJose pequena />
              <div className="jose-burbuja">
                <p className="jose-pregunta" id={`jose-${actual}`}>
                  {t.oportunidad.cualificacion.preguntas[actual]}
                </p>
                {pistaHistorial(actual)}
                <Fichas
                  pregunta={actual}
                  marcada={null}
                  deshabilitado={deshabilitado}
                  onElegir={(x) => void responder(actual, x)}
                  sugerida={sugerencia(actual)}
                />
                <button type="button" className="jose-cambiar" onClick={() => setSaltadas((s) => new Set([...s, actual]))}>
                  {t.jose.saltar}
                </button>
              </div>
            </div>
          </li>
        ) : null}
      </ol>

      {!abierta ? <p className="ayuda-neutra">{t.jose.cerrada}</p> : null}

      {actual === null && pendientes.length > 0 ? (
        <div className="jose-pendientes">
          <p className="ayuda-neutra">{t.jose.saltadas}</p>
          <ul>
            {pendientes.map((p) => (
              <li key={p}>
                <button
                  type="button"
                  className="jose-cambiar"
                  onClick={() =>
                    setSaltadas((s) => {
                      const n = new Set(s);
                      n.delete(p);
                      return n;
                    })
                  }
                >
                  {t.jose.responder}: {t.oportunidad.cualificacion.preguntas[p]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {completa(q) && oportunidad.status === "OPEN" ? (
        <div className="jose-resumen">
          <p className="jose-nombre">{t.jose.resumenTitulo}</p>
          <p>
            {t.jose.resumen(
              t.jose.tramos[r.tramo],
              r.riesgo === null ? null : t.jose.riesgos[r.riesgo.caso][r.riesgo.pregunta],
              r.pero,
            )}
          </p>
        </div>
      ) : null}

      {pasoAparte !== null ? (
        <div className="jose-paso">
          <p className="jose-nombre">{t.jose.pasoTitulo}</p>
          <p>{textoPaso(pasoAparte).frase}</p>
          {botonApuntar(pasoAparte, t.jose.apuntar, t.jose.apuntado)}
          <TeHaServido key={clavePaso(pasoAparte.clave)} asunto={clavePaso(pasoAparte.clave)} />
        </div>
      ) : null}
    </div>
  );
}
